import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApps } from '@/contexts/AppsContext';
import { billingApi, apiKeysApi } from '@/services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDate, formatNumber, formatBytes } from '@/lib/utils';
import { ArrowLeft, Pencil, Key, Plus, Trash2, Copy, Eye, EyeOff } from 'lucide-react';

export default function AppDetailPage() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const { fetchApp, deleteApp, selectApp } = useApps();
  const [app, setApp] = useState(null);
  const [usage, setUsage] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showKeys, setShowKeys] = useState({});

  useEffect(() => {
    const loadData = async () => {
      try {
        const [appData, usageData, keysData] = await Promise.all([
          fetchApp(appId),
          billingApi.getUsage(appId).catch(() => ({ data: { data: null } })),
          apiKeysApi.list(appId).catch(() => ({ data: { data: [] } })),
        ]);

        setApp(appData);
        selectApp(appData);
        setUsage(usageData.data?.data);
        const keys = keysData.data?.data;
        setApiKeys(Array.isArray(keys) ? keys : []);
      } catch (err) {
        console.error('Failed to load app:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [appId, fetchApp, selectApp]);

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete "${app?.name}"? This action cannot be undone.`)) {
      await deleteApp(appId);
      navigate('/apps');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const toggleKeyVisibility = (keyId) => {
    setShowKeys((prev) => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!app) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">App not found</h2>
        <p className="text-muted-foreground mb-4">
          The app you're looking for doesn't exist or has been deleted.
        </p>
        <Button onClick={() => navigate('/apps')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Apps
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate('/apps')}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Apps
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <h1 className="text-2xl font-bold">{app.name}</h1>
            <Badge variant={app.status === 'active' ? 'success' : 'secondary'}>
              {app.status}
            </Badge>
          </div>
          <p className="text-muted-foreground mt-1">
            {app.description || 'No description'}
          </p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" asChild>
            <Link to={`/apps/${appId}/edit`}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Link>
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="keys">API Keys</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Info Cards */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  App ID
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2">
                  <code className="text-sm bg-muted px-2 py-1 rounded truncate flex-1">
                    {app.appId}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => copyToClipboard(app.appId)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Plan
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold capitalize">
                    {app.planTier || 'Free'}
                  </span>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/billing">Upgrade</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Created
                </CardTitle>
              </CardHeader>
              <CardContent>
                <span className="text-lg font-semibold">
                  {formatDate(app.createdAt)}
                </span>
              </CardContent>
            </Card>
          </div>

          {/* Usage */}
          {usage && (
            <Card>
              <CardHeader>
                <CardTitle>Current Usage</CardTitle>
                <CardDescription>Usage for the current billing period</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>API Calls</span>
                      <span className="text-muted-foreground">
                        {formatNumber(usage.apiCalls || 0)} / {formatNumber(usage.limits?.apiCalls || 0)}
                      </span>
                    </div>
                    <Progress value={usage.apiCallsPercent || 0} />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Storage</span>
                      <span className="text-muted-foreground">
                        {formatBytes(usage.storageBytes || 0)} / {usage.limits?.storageGb || 0} GB
                      </span>
                    </div>
                    <Progress value={usage.storagePercent || 0} />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="keys" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">API Keys</h3>
              <p className="text-sm text-muted-foreground">
                Manage API keys for this application
              </p>
            </div>
            <Button asChild>
              <Link to={`/keys/new?app=${appId}`}>
                <Plus className="mr-2 h-4 w-4" />
                Create Key
              </Link>
            </Button>
          </div>

          {apiKeys.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-8">
                <Key className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No API keys yet</p>
                <Button asChild>
                  <Link to={`/keys/new?app=${appId}`}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create API Key
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {apiKeys.map((key) => (
                <Card key={key.keyId}>
                  <CardContent className="flex items-center justify-between py-4">
                    <div>
                      <p className="font-medium">{key.name || 'Unnamed Key'}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <code className="text-xs bg-muted px-2 py-1 rounded">
                          {showKeys[key.keyId]
                            ? key.keyPrefix + '...'
                            : '••••••••••••••••'}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => toggleKeyVisibility(key.keyId)}
                        >
                          {showKeys[key.keyId] ? (
                            <EyeOff className="h-3 w-3" />
                          ) : (
                            <Eye className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant={key.revoked ? 'destructive' : 'success'}>
                        {key.revoked ? 'Revoked' : 'Active'}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        Created {formatDate(key.createdAt)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>App Settings</CardTitle>
              <CardDescription>Configure your application settings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">App Name</Label>
                  <p className="text-muted-foreground">{app.name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Description</Label>
                  <p className="text-muted-foreground">
                    {app.description || 'No description'}
                  </p>
                </div>
                <Button variant="outline" asChild>
                  <Link to={`/apps/${appId}/edit`}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit Settings
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Label({ children, className }) {
  return <label className={className}>{children}</label>;
}
